import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import {useState} from 'react';
import {MassiveAction} from '../state/organization-details-page-state';
import {CreateOrganizationLicensePlanDrawer} from './CreateOrganizationLicensePlanDrawer';

type Props = {
  massiveAction: MassiveAction | null
  setMassiveAction: (massiveAction: MassiveAction) => void
  onConfirmSave: () => Promise<void>;
  translateLocal: (key: string) => string
  translate: (key: string) => string

  isSaveDisabled: boolean

  createLicensePlan: (subscriptionType: string) => Promise<void>
  isCreateLicensePlanDrawerOpen: boolean
  setCreateLicensePlanDrawerOpen: (value: boolean) => void

  recalculateLicense: () => Promise<void>
  updateDiscount: (discount: number) => Promise<void>
  currentDiscount: number
}

export const OrganizationLicenseDetailsActionsBar = ({ massiveAction, setMassiveAction, onConfirmSave, isCreateLicensePlanDrawerOpen, setCreateLicensePlanDrawerOpen, createLicensePlan, recalculateLicense, updateDiscount, currentDiscount, isSaveDisabled, translateLocal, translate }: Props) => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openRecalc, setOpenRecalc] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [openDiscount, setOpenDiscount] = useState(false);
  const [discountInput, setDiscountInput] = useState('');
  const [updatingDiscount, setUpdatingDiscount] = useState(false);

  const handleRecalcConfirm = async () => {
    setRecalculating(true);
    try {
      await recalculateLicense();
    } finally {
      setRecalculating(false);
      setOpenRecalc(false);
    }
  };

  const handleDiscountConfirm = async () => {
    setUpdatingDiscount(true);
    try {
      await updateDiscount(parseFloat(discountInput));
    } finally {
      setUpdatingDiscount(false);
      setOpenDiscount(false);
    }
  };

  const handleSaveClick = () => {
    if(massiveAction === 'create_plan') {
      setCreateLicensePlanDrawerOpen(true)
    } else {
      setOpenConfirm(true);
    }
  };

  const handleConfirm = async () => {
    if (massiveAction && onConfirmSave) {
      await onConfirmSave();
    }
    setOpenConfirm(false);
  };

  const handleCancel = () => {
    setOpenConfirm(false);
  };

  return (
    <Box sx={{ pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Select
          labelId="demo-select-label"
          value={massiveAction}
          onChange={(event) => setMassiveAction(event.target.value as MassiveAction)}
          sx={{
            width: 250,
            mr: 2,
            '& .MuiSelect-select': {
              padding: '8px 8px',
            },
          }}
        >
          <MenuItem value={'no_action'}><em>{translateLocal('actions.no_action.title')}</em></MenuItem>
          <MenuItem value={'create_plan'}>{translateLocal('actions.create_plan.title')}</MenuItem>
          <MenuItem value={'update_status'}>{translateLocal('actions.update_status.title')}</MenuItem>
          <MenuItem value={'update_subscription_type'}>{translateLocal('actions.update_subscription_type.title')}</MenuItem>
          <MenuItem value={'update_trial_date'}>{translateLocal('actions.update_trial_date.title')}</MenuItem>
        </Select>
        {
          massiveAction === 'no_action'
            ? <></>
            : <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" color="primary" onClick={handleSaveClick} disabled={isSaveDisabled}>Zapisz</Button>
              <Button variant="outlined" onClick={() => setMassiveAction('no_action')}>Anuluj</Button>
            </Box>
        }
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => { setDiscountInput(currentDiscount.toString()); setOpenDiscount(true); }}
            disabled={updatingDiscount}
          >
            Aktualizuj rabat
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setOpenRecalc(true)}
            disabled={recalculating}
          >
            {recalculating ? 'Przeliczanie…' : 'Przelicz cenę'}
          </Button>
        </Box>

        <CreateOrganizationLicensePlanDrawer
          createLicensePlan={createLicensePlan}
          isDrawerOpen={isCreateLicensePlanDrawerOpen}
          onDrawerClosed={() => setCreateLicensePlanDrawerOpen(false)}
        />
      </Box>

      <Drawer
        anchor="right"
        open={openDiscount}
        onClose={() => !updatingDiscount && setOpenDiscount(false)}
        PaperProps={{
          sx: {
            width: 480,
            marginTop: '64px',
            height: 'calc(100% - 64px)',
            position: 'fixed',
            right: 0,
            padding: 3,
          }
        }}
      >
        <h2>Aktualizuj rabat</h2>
        <Stack spacing={3}>
          <TextField
            autoFocus
            type="number"
            label="Rabat [%]"
            value={discountInput}
            onChange={(e) => setDiscountInput(e.target.value)}
            inputProps={{ min: 0, max: 100, step: 0.01 }}
            fullWidth
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleDiscountConfirm}
            disabled={updatingDiscount || discountInput === ''}
          >
            {translate('common.save.title')}
          </Button>
          <Button variant="outlined" onClick={() => setOpenDiscount(false)} disabled={updatingDiscount}>
            {translate('common.cancel.title')}
          </Button>
        </Stack>
      </Drawer>

      <Dialog open={openConfirm} onClose={handleCancel}>
        <DialogTitle>{translateLocal('actions.confirm.title')}</DialogTitle>
        <DialogContent>
          <Typography>{translateLocal('actions.confirm.description')} {translateLocal(`actions.${massiveAction}.title`)}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>{translate('common.cancel.title')}</Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">{translate('common.save.title')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openRecalc} onClose={() => !recalculating && setOpenRecalc(false)}>
        <DialogTitle>Przeliczyć cenę licencji?</DialogTitle>
        <DialogContent>
          <Typography>
            Ceny funkcjonalności i baza licencji zostaną zaktualizowane wg bieżącego katalogu,
            a rabat przeliczony. Historia nadań (log) pozostaje bez zmian.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRecalc(false)} disabled={recalculating}>{translate('common.cancel.title')}</Button>
          <Button onClick={handleRecalcConfirm} variant="contained" color="primary" disabled={recalculating}>Przelicz</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};